import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
} from '@mui/material';
import { Close } from '@mui/icons-material';
import styles from './AIPromptDialog.module.scss';

// ─── The AI prompt ───────────────────────────────────────────────────────────

export const AI_BLOG_PROMPT = `You are a professional blog writer. Respond with ONLY formatted blog content — no explanations, no meta-commentary, no preamble.

Use this EXACT Markdown formatting so the content imports cleanly into a rich-text editor:

BLOCK FORMATTING:
  ## Section Title   → H2 heading (use for major sections)
  ### Subsection     → H3 heading (use for sub-topics)
  ---                → horizontal divider (on its own line, between major sections)
  > text             → highlighted callout or blockquote

INLINE FORMATTING:
  **text**           → bold (for key terms and important phrases)
  *text*             → italic (for emphasis, titles, foreign words)
  \`text\`             → inline code (for technical terms, commands)
  [label](url)       → hyperlink

LISTS:
  - item             → bullet list (features, tips, pros/cons — 3+ items)
  1. item            → numbered list (step-by-step instructions, rankings)

STRUCTURE RULES:
  • Do NOT start with an H1 (#) — the blog title is set separately
  • Start directly with a ## heading for the first section
  • Leave ONE blank line between every block (paragraph, heading, list, divider)
  • Keep paragraphs to 2–4 sentences
  • Use bullet or numbered lists whenever you list 3 or more items
  • Add a ## Conclusion or ## Key Takeaways section at the end
  • Vary structure — mix paragraphs, lists, and callouts throughout

EXAMPLE OUTPUT FORMAT:

## Introduction

Opening paragraph goes here. Keep it engaging and set context for the reader.

## Why This Matters

A short paragraph explaining relevance.

- **Key benefit one** — brief explanation
- **Key benefit two** — brief explanation
- **Key benefit three** — brief explanation

## How It Works

Step-by-step explanation or deeper dive.

1. First step with clear instructions
2. Second step building on the first
3. Third step completing the process

> **Pro tip:** Add a standout insight or important warning here as a callout.

---

## Conclusion

Closing thoughts that summarise the value and encourage action.

---
TOPIC: [Replace this line with your blog topic, brief, or outline]`;

// ─── Inline markdown parser → Tiptap inline nodes ────────────────────────────

function parseInline(text) {
  if (!text) return [];
  const nodes = [];
  // Order matters: ** before *, link before plain text
  const regex = /(\*\*(.+?)\*\*)|(\*(.+?)\*)|(`(.+?)`)|(\[([^\]]+)\]\(([^)]+)\))/g;
  let lastIndex = 0;
  let match;

  while ((match = regex.exec(text)) !== null) {
    if (match.index > lastIndex) {
      nodes.push({ type: 'text', text: text.slice(lastIndex, match.index) });
    }
    if (match[1]) {
      // **bold**
      nodes.push({ type: 'text', text: match[2], marks: [{ type: 'bold' }] });
    } else if (match[3]) {
      // *italic*
      nodes.push({ type: 'text', text: match[4], marks: [{ type: 'italic' }] });
    } else if (match[5]) {
      // `code`
      nodes.push({ type: 'text', text: match[6], marks: [{ type: 'code' }] });
    } else if (match[7]) {
      // [link](url)
      nodes.push({
        type: 'text',
        text: match[8],
        marks: [{ type: 'link', attrs: { href: match[9], target: '_blank', rel: 'noopener noreferrer' } }],
      });
    }
    lastIndex = regex.lastIndex;
  }

  if (lastIndex < text.length) {
    nodes.push({ type: 'text', text: text.slice(lastIndex) });
  }

  return nodes.length ? nodes : [];
}

// ─── Block markdown parser → Tiptap JSON ─────────────────────────────────────

export function parseMarkdownToTiptap(markdown) {
  const lines = markdown.split('\n');
  const nodes = [];
  let i = 0;

  while (i < lines.length) {
    const line = lines[i];
    const trimmed = line.trim();

    // ── Fenced code block ───────────────────────────────────────────────────
    if (trimmed.startsWith('```')) {
      const language = trimmed.slice(3).trim();
      const codeLines = [];
      i++;
      while (i < lines.length && !lines[i].trim().startsWith('```')) {
        codeLines.push(lines[i]);
        i++;
      }
      nodes.push({
        type: 'codeBlock',
        attrs: { language: language || null },
        content: codeLines.length ? [{ type: 'text', text: codeLines.join('\n') }] : [],
      });
      i++; // skip closing ```
      continue;
    }

    // ── Horizontal rule ─────────────────────────────────────────────────────
    if (/^[-*_]{3,}\s*$/.test(trimmed)) {
      nodes.push({ type: 'horizontalRule' });
      i++;
      continue;
    }

    // ── Heading ─────────────────────────────────────────────────────────────
    const headingMatch = line.match(/^(#{1,6})\s+(.+)/);
    if (headingMatch) {
      const inline = parseInline(headingMatch[2].trim());
      nodes.push({
        type: 'heading',
        attrs: { level: headingMatch[1].length },
        content: inline.length ? inline : [{ type: 'text', text: headingMatch[2].trim() }],
      });
      i++;
      continue;
    }

    // ── Blockquote ──────────────────────────────────────────────────────────
    if (line.startsWith('> ') || line === '>') {
      const quoteLines = [];
      while (i < lines.length && (lines[i].startsWith('> ') || lines[i] === '>')) {
        quoteLines.push(lines[i].replace(/^>\s?/, ''));
        i++;
      }
      const quoteText = quoteLines.join(' ').trim();
      nodes.push({
        type: 'blockquote',
        content: [{ type: 'paragraph', content: parseInline(quoteText) }],
      });
      continue;
    }

    // ── Bullet list ─────────────────────────────────────────────────────────
    if (/^[-*+]\s/.test(line)) {
      const items = [];
      while (i < lines.length && /^[-*+]\s/.test(lines[i])) {
        const itemText = lines[i].replace(/^[-*+]\s/, '');
        const inline = parseInline(itemText);
        items.push({
          type: 'listItem',
          content: [{ type: 'paragraph', content: inline.length ? inline : [{ type: 'text', text: itemText }] }],
        });
        i++;
      }
      nodes.push({ type: 'bulletList', content: items });
      continue;
    }

    // ── Ordered list ────────────────────────────────────────────────────────
    if (/^\d+\.\s/.test(line)) {
      const items = [];
      while (i < lines.length && /^\d+\.\s/.test(lines[i])) {
        const itemText = lines[i].replace(/^\d+\.\s/, '');
        const inline = parseInline(itemText);
        items.push({
          type: 'listItem',
          content: [{ type: 'paragraph', content: inline.length ? inline : [{ type: 'text', text: itemText }] }],
        });
        i++;
      }
      nodes.push({ type: 'orderedList', content: items });
      continue;
    }

    // ── Empty line ──────────────────────────────────────────────────────────
    if (trimmed === '') {
      i++;
      continue;
    }

    // ── Paragraph (collect consecutive non-block lines) ─────────────────────
    const paraLines = [];
    while (
      i < lines.length &&
      lines[i].trim() !== '' &&
      !lines[i].match(/^#{1,6}\s/) &&
      !/^[-*+]\s/.test(lines[i]) &&
      !/^\d+\.\s/.test(lines[i]) &&
      !lines[i].startsWith('> ') &&
      !lines[i].trim().startsWith('```') &&
      !/^[-*_]{3,}\s*$/.test(lines[i].trim())
    ) {
      paraLines.push(lines[i]);
      i++;
    }

    if (paraLines.length) {
      const paraText = paraLines.join(' ');
      const inline = parseInline(paraText);
      nodes.push({
        type: 'paragraph',
        content: inline.length ? inline : [{ type: 'text', text: paraText }],
      });
    }
  }

  if (nodes.length === 0) nodes.push({ type: 'paragraph' });
  return { type: 'doc', content: nodes };
}

// ─── Component ───────────────────────────────────────────────────────────────

export default function AIPromptDialog({ open, onClose, onImport }) {
  const [copied, setCopied] = useState(false);
  const [advancedOpen, setAdvancedOpen] = useState(false);
  const [importText, setImportText] = useState('');
  const [importError, setImportError] = useState('');

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(AI_BLOG_PROMPT);
      setCopied(true);
      setTimeout(() => setCopied(false), 2200);
    } catch {
      // fallback for older browsers
      const ta = document.createElement('textarea');
      ta.value = AI_BLOG_PROMPT;
      document.body.appendChild(ta);
      ta.select();
      document.execCommand('copy');
      document.body.removeChild(ta);
      setCopied(true);
      setTimeout(() => setCopied(false), 2200);
    }
  };

  const handleImport = () => {
    if (!importText.trim()) {
      setImportError('Paste the AI response above first.');
      return;
    }
    const tiptapJson = parseMarkdownToTiptap(importText);
    onImport(tiptapJson);
    setImportText('');
    setImportError('');
    setAdvancedOpen(false);
    onClose();
  };

  const handleClose = () => {
    setImportText('');
    setImportError('');
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 1 }}>
        <span className={styles.dialogTitleText}>
          <span className={styles.sparkIcon}>✦</span>
          AI Content Prompt
        </span>
        <IconButton size="small" onClick={handleClose}>
          <Close fontSize="small" />
        </IconButton>
      </DialogTitle>

      <div className={styles.dialogBody}>
        <p className={styles.description}>
          Copy this prompt, paste it into any AI assistant (ChatGPT, Claude, Gemini…), replace the <strong>TOPIC</strong> line, and get back perfectly structured blog content. Use <strong>Advanced Import</strong> below to load the AI response directly into the editor without copy-pasting each section.
        </p>

        <div className={styles.promptBox}>
          <pre className={styles.promptText}>{AI_BLOG_PROMPT}</pre>
        </div>

        <div className={styles.copyRow}>
          <button className={`${styles.copyBtn} ${copied ? styles.copyBtnSuccess : ''}`} onClick={handleCopy}>
            {copied ? (
              <>
                <span className={styles.btnIcon}>✓</span>
                Copied to Clipboard!
              </>
            ) : (
              <>
                <span className={styles.btnIcon}>⧉</span>
                Copy Prompt
              </>
            )}
          </button>
          <span className={styles.copyHint}>Works with ChatGPT, Claude, Gemini and any AI assistant</span>
        </div>

        <button
          className={styles.advancedToggle}
          onClick={() => setAdvancedOpen((p) => !p)}
          type="button"
        >
          <span className={styles.chevron}>{advancedOpen ? '▾' : '▸'}</span>
          Advanced Import — Paste AI Response &amp; Auto-Format
        </button>

        {advancedOpen && (
          <div className={styles.importArea}>
            <p className={styles.importHint}>
              Paste the full markdown response from the AI below. The editor will parse headings, lists, bold, italic, code blocks, dividers, and more automatically.
            </p>
            <textarea
              className={styles.importTextarea}
              rows={14}
              placeholder="Paste the AI's markdown response here…"
              value={importText}
              onChange={(e) => {
                setImportText(e.target.value);
                setImportError('');
              }}
            />
            {importError && <p className={styles.importError}>{importError}</p>}
            <div className={styles.importActions}>
              <button className={styles.importBtn} onClick={handleImport} type="button">
                Import to Editor
              </button>
              <button
                className={styles.clearBtn}
                onClick={() => { setImportText(''); setImportError(''); }}
                type="button"
              >
                Clear
              </button>
            </div>
          </div>
        )}
      </div>
    </Dialog>
  );
}
